import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import Select from "react-select";
import { toast } from "react-toastify";
import { getCookies } from "../../hooks/useCookies";
import {
  REACT_APP_API_DRINK_IDENTITY_TAGS,
  REACT_APP_API_DRINK_IDENTITY_TAG_ASSIGNMENTS,
} from "../../redux/apiConstants";
import styles from "./index.module.scss";

const getAuthHeaders = () => ({
  Authorization: `Bearer ${getCookies("Token")}`,
});

const tagIdOf = (tag) => {
  if (tag == null || tag === "") return "";
  if (typeof tag !== "object") return String(tag);
  const raw = tag.id || tag._id || tag.tag;
  if (raw == null || raw === "") return "";
  if (typeof raw === "object") {
    if (raw.id || raw._id) return String(raw.id || raw._id);
    return String(raw);
  }
  return String(raw);
};

const DrinkIdentityTagAssigner = ({
  mode,
  drinkId,
  addonId,
  modifierKey,
  disabled = false,
  compact = false,
  onSaved,
}) => {
  const [allTags, setAllTags] = useState([]);
  const [selected, setSelected] = useState([]);
  const [defaultTagIds, setDefaultTagIds] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const canLoad =
    mode === "menu_item"
      ? Boolean(drinkId)
      : Boolean(addonId && modifierKey);

  const toOption = useCallback((t) => {
    return {
      value: tagIdOf(t),
      label: t.displayName || t.key || tagIdOf(t),
    };
  }, []);

  const loadAssignment = useCallback(
    async (tagLibrary) => {
      if (!canLoad) return;
      let url;
      if (mode === "menu_item") {
        url = `${REACT_APP_API_DRINK_IDENTITY_TAG_ASSIGNMENTS}/menu-item/${drinkId}`;
      } else {
        url = `${REACT_APP_API_DRINK_IDENTITY_TAG_ASSIGNMENTS}/modifier/${addonId}/${encodeURIComponent(
          modifierKey
        )}`;
      }
      const { data } = await axios.get(url, { headers: getAuthHeaders() });
      const assignment = data?.data?.assignment || data?.assignment || {};
      const tags = assignment?.tags || [];
      const libraryById = new Map(
        (tagLibrary || []).map((t) => [tagIdOf(t), t])
      );
      setSelected(
        tags
          .map((t) => {
            const full = libraryById.get(tagIdOf(t)) || t;
            return toOption(full);
          })
          .filter((opt) => opt.value)
      );

      const categoryAware =
        mode === "menu_item" &&
        assignment &&
        (Object.prototype.hasOwnProperty.call(assignment, "defaults") ||
          Object.prototype.hasOwnProperty.call(assignment, "excluded") ||
          Object.prototype.hasOwnProperty.call(assignment, "extras"));
      if (categoryAware) {
        setDefaultTagIds(
          new Set(
            (assignment.defaults || []).map((t) => tagIdOf(t)).filter(Boolean)
          )
        );
      } else {
        setDefaultTagIds(null);
      }
    },
    [addonId, canLoad, drinkId, mode, modifierKey, toOption]
  );

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!canLoad) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: "1",
          limit: "200",
          sortBy: "order:asc",
        });
        const { data } = await axios.get(
          `${REACT_APP_API_DRINK_IDENTITY_TAGS}?${params}`,
          { headers: getAuthHeaders() }
        );
        const results = (data?.data?.result?.results || []).filter(
          (t) => t.active !== false
        );
        if (cancelled) return;
        setAllTags(results);
        await loadAssignment(results);
      } catch {
        if (!cancelled) toast.error("Failed to load identity tags");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [canLoad, loadAssignment]);

  const options = useMemo(() => allTags.map(toOption), [allTags, toOption]);

  const categoryAware = mode === "menu_item" && defaultTagIds !== null;

  const formatOptionLabel = useCallback(
    (option, { context }) => {
      if (!categoryAware) return option.label;
      const inherited = defaultTagIds.has(option.value);
      const showExtra = context === "value" && !inherited;
      if (!inherited && !showExtra) return option.label;
      return (
        <span>
          {option.label}
          <span
            className={`${styles.kindLabel} ${
              inherited ? styles.kindDefault : styles.kindExtra
            }`}
          >
            {inherited ? "Default" : "Extra"}
          </span>
        </span>
      );
    },
    [categoryAware, defaultTagIds]
  );

  const handleSave = async () => {
    if (!canLoad || disabled) return;
    setSaving(true);
    try {
      const tagIds = selected.map((s) => s.value).filter(Boolean);
      let url;
      if (mode === "menu_item") {
        url = `${REACT_APP_API_DRINK_IDENTITY_TAG_ASSIGNMENTS}/menu-item/${drinkId}`;
      } else {
        url = `${REACT_APP_API_DRINK_IDENTITY_TAG_ASSIGNMENTS}/modifier/${addonId}/${encodeURIComponent(
          modifierKey
        )}`;
      }
      await axios.put(url, { tagIds }, { headers: getAuthHeaders() });
      toast.success("Identity tags saved");
      if (onSaved) onSaved(tagIds);
      await loadAssignment(allTags);
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to save identity tags"
      );
    } finally {
      setSaving(false);
    }
  };

  if (!canLoad) {
    return (
      <div className={styles.wrap}>
        <p className={styles.hint}>Save the item first to assign identity tags.</p>
      </div>
    );
  }

  return (
    <div className={`${styles.wrap} ${compact ? styles.compact : ""}`}>
      {!compact && (
        <label className={styles.label} htmlFor="drink-identity-tag-select">
          Drink Identity Tags
        </label>
      )}
      <Select
        inputId="drink-identity-tag-select"
        isMulti
        isDisabled={disabled || loading || saving}
        options={options}
        value={selected}
        onChange={(val) => setSelected(val || [])}
        formatOptionLabel={formatOptionLabel}
        placeholder={loading ? "Loading tags..." : "Select identity tags"}
        classNamePrefix="identity-tag"
        aria-label="Drink identity tags"
        maxMenuHeight={180}
        menuPlacement="auto"
        menuPortalTarget={
          typeof document !== "undefined" ? document.body : null
        }
        styles={{
          control: (base, state) => ({
            ...base,
            minHeight: 38,
            borderColor: state.isFocused ? "#39979d" : "#ccc",
            boxShadow: state.isFocused
              ? "0 0 0 0.2rem rgba(57, 151, 157, 0.15)"
              : "none",
            fontSize: 14,
            "&:hover": { borderColor: "#39979d" },
          }),
          multiValue: (base) => ({
            ...base,
            backgroundColor: "#eef6f7",
          }),
          multiValueLabel: (base) => ({
            ...base,
            color: "#39979d",
          }),
          option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected
              ? "#39979d"
              : state.isFocused
                ? "#eef6f7"
                : base.backgroundColor,
            color: state.isSelected ? "#fff" : "#1d1d1d",
          }),
          menuPortal: (base) => ({ ...base, zIndex: 2100 }),
        }}
      />
      {categoryAware && (
        <p className={styles.hint}>
          Defaults come from this drink type. Uncheck one to exclude it on this
          item. Any other tag you add is an extra.
        </p>
      )}
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.saveBtn}
          onClick={handleSave}
          disabled={disabled || loading || saving}
          aria-label="Save identity tags"
        >
          {saving ? "Saving..." : "Save Tags"}
        </button>
      </div>
    </div>
  );
};

DrinkIdentityTagAssigner.propTypes = {
  mode: PropTypes.oneOf(["menu_item", "modifier"]).isRequired,
  drinkId: PropTypes.string,
  addonId: PropTypes.string,
  modifierKey: PropTypes.string,
  drinkTypeId: PropTypes.string,
  disabled: PropTypes.bool,
  compact: PropTypes.bool,
  onSaved: PropTypes.func,
};

export default DrinkIdentityTagAssigner;
